/* My PT Clients.
 *
 * A coach's people come in TWO GROUPS and this screen keeps them apart:
 *
 *   PT CLIENTS    the personal-training roster: theirs by assignment.
 *   TRAIN WITH ME everyone whose coach is this trainer because they keep turning up
 *                 to their sessions. Attending somebody's class puts you on their
 *                 list; it does not make you their client.
 *
 * ORDER IS THE RISK MODEL, and the two populations it declines to score sit
 * underneath it, UNRANKED: first-90-day members on their track, then anyone on Care
 * Track or Hold. A PT client on Care Track is still on the roster, because they are
 * still a client, but they are never given a number (SITE-RULES 1).
 *
 * The screen it replaces opened on four stat tiles, a trainer id printed on screen,
 * and three tables headed by a Health column. None of it named who to talk to first.
 */
import type { Member } from '@acrostic/model';
import { notScoredBecause } from '@acrostic/model';
import { useAsync, useSource } from '../data/SourceProvider.js';
import { MAX_PAGE } from '../data/SeedSource.js';
import { NoneRow, Panel } from '../components/Panel.js';
import { Avatar } from '../components/Avatar.js';
import { tone } from '../components/People.js';
import { fmt } from '../lib/fmt.js';
import { go } from '../routes.js';
import type { ScreenProps } from '../App.js';

/** 0 scored and ranked · 1 first 90 days, on the track · 2 Care Track or Hold. */
function rank(m: Member): 0 | 1 | 2 {
  if (m.suppression) return 2;
  return notScoredBecause(m) ? 1 : 0;
}

/* 101 parks the unscored above a perfect 100 so they can never lead a ranked list.
   rank() already separates them; this only matters if that ordering ever changes. */
function order(a: Member, b: Member): number {
  const ra = rank(a);
  const rb = rank(b);
  const sa = ra === 0 ? a.risk?.score ?? 101 : 101;
  const sb = rb === 0 ? b.risk?.score ?? 101 : 101;
  return ra - rb || sa - sb || a.name.localeCompare(b.name);
}

const needsWord = (m: Member): boolean => rank(m) === 0 && (m.risk?.tier ?? 'Green') !== 'Green';

const SHOW = 12;

export function Coach({ user }: ScreenProps) {
  const source = useSource();
  /* TWO LISTS, ASKED FOR SEPARATELY, because they are two questions and the counts
     beside them have to be true.
     
     This used to read ONE page of a hundred and slice it into the two panels here,
     so a coach with three hundred people was told they had a hundred - and the link
     offering "All 88 of yours" was offering 88 of some larger number nobody named.
     It is the same mistake the lead board's header was making, and the contract in
     data/contract.ts already forbids it in as many words: a list is always paged,
     filtered and sorted by the SOURCE, never a roster handed over for the screen to
     slice.
     
     The source filters by coach id, never by display name: matching on that is how
     "Coach Jane D." and "Jane Doe" became two people. */
  const { data: ptPage, error } = useAsync(
    () => source.getMembers({ coachId: user.id, status: 'current', pt: true, size: MAX_PAGE }),
    [source, user.id],
  );
  const { data: restPage, error: restError } = useAsync(
    () => source.getMembers({ coachId: user.id, status: 'current', pt: false, size: MAX_PAGE }),
    [source, user.id],
  );

  const err = error ?? restError;
  if (err) return <NoneRow>Could not read your clients. {err.message}</NoneRow>;
  if (!ptPage || !restPage) return <NoneRow>Reading your roster…</NoneRow>;

  if (!ptPage.total && !restPage.total) {
    return (
      <Panel title="Nobody is on your roster yet">
        <p className="t-body-small muted">
          Clients attach to you from the sessions you teach and from a manager assigning
          them on the member record. This login does not have anybody yet.
        </p>
      </Panel>
    );
  }

  /* PT IS A RELATIONSHIP, NOT A PLAN NAME. It was read off the plan string, which
     stopped working the moment a coach stopped being sent plans at all - and that
     was the right change, so `pt` is its own field on the member, and the source is
     asked about it rather than the rows being sorted through here. It says a coach
     trains them one to one. It says nothing about what they pay. */
  const pt = ptPage.items.slice().sort(order);
  const rest = restPage.items.slice().sort(order);

  return (
    <div className="grid2">
      <Panel
        eyebrow={`${fmt.num(ptPage.total)} ${ptPage.total === 1 ? 'client' : 'clients'}`
          + `${ptPage.total ? word(pt, ptPage.total) : ''}`}
        title="PT clients"
      >
        <Rows list={pt} empty="No PT clients on your roster yet. A manager assigns them on the member record." />
      </Panel>

      <Panel
        eyebrow={`${fmt.num(restPage.total)} train with you outside PT`
          + `${restPage.total ? word(rest, restPage.total) : ''}`}
        title="Train with you"
        right={restPage.total > SHOW
          ? <a className="linkish t-label-small" href="#/members">All {fmt.num(restPage.total)} of yours</a>
          : undefined}
      >
        <Rows list={rest.slice(0, SHOW)} empty="Nobody trains in your sessions yet outside PT." />
      </Panel>
    </div>
  );
}

/* "Nobody needs a word today" is a claim about the WHOLE book, so it is only made
   when the whole book was read. Past the page ceiling the coach is looking at the
   worst hundred of a longer list, and the honest sentence is that there are at least
   this many - saying "nobody" there would be the one reading that is certainly
   wrong. */
function word(loaded: Member[], total: number): string {
  const n = loaded.filter(needsWord).length;
  const all = loaded.length >= total;
  if (n) return ` · ${fmt.num(n)}${all ? '' : '+'} need a word`;
  return all ? ' · nobody needs a word today' : '';
}

function Rows({ list, empty }: { list: Member[]; empty: string }) {
  if (!list.length) return <NoneRow>{empty}</NoneRow>;
  return (
    <div className="rows">
      {list.map((m) => {
        const off = notScoredBecause(m);
        const score = off ? null : m.risk?.score ?? null;
        const bits = [
          m.plan,
          m.lastVisit ? 'last visit ' + fmt.ago(m.lastVisit) : 'no visit on record',
          off,
        ].filter(Boolean);   // plan is null on this seat unless the gym turned it on
        return (
          <button key={m.id} type="button" className="row clickable" onClick={() => go('/members/' + m.id)}>
            {score == null
              ? <span className="dot" style={{ background: 'transparent' }} aria-hidden="true" />
              : <span className={'dot ' + tone(score)} aria-hidden="true" />}
            <Avatar name={m.name} photoUrl={m.photoUrl} size="sm" />
            <div className="who">
              <div className="n t-label-large">
                {m.name}
                {!m.anchors.length && <span className="pill warn t-caption">No anchor</span>}
              </div>
              <div className="s t-caption muted">{bits.join(' · ')}</div>
            </div>
            {score == null
              ? <span className="pill flat t-caption">Not scored</span>
              : <span className="score t-label-medium">{score}</span>}
          </button>
        );
      })}
    </div>
  );
}
